import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import { useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const FEATURES = [
  {
    icon: <CategoryRoundedIcon />,
    title: "Ítems generales",
    description: "Una base común para productos, insumos, repuestos, servicios y otros elementos del negocio.",
  },
  {
    icon: <CompareArrowsRoundedIcon />,
    title: "Movimientos",
    description: "Entradas, salidas, ajustes, transferencias y trazabilidad de cada operación.",
  },
  {
    icon: <WarehouseRoundedIcon />,
    title: "Ubicaciones y depósitos",
    description: "Preparado para separar stock por depósito, local, sucursal o ubicación interna.",
  },
  {
    icon: <InventoryRoundedIcon />,
    title: "Control operativo",
    description: "Una capa interna distinta del catálogo público y de los pedidos del e-commerce.",
  },
];

export default function ProjectSystemPage() {
  const { projectId } = useParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject]
  );
  const enabled = isProjectModuleEnabled(project || contextProject, "system");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    projectsApi.get(projectId)
      .then((data) => {
        if (!active) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (active) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  if (loading) {
    return <Alert severity="info">Cargando módulo...</Alert>;
  }

  if (!enabled && !loading) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Sistema"
        description="Este nivel agrega ítems generales y movimientos internos para administrar el negocio más allá del catálogo público."
      />
    );
  }

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Sistema
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            {project?.name || contextProject?.name || projectId} · operación interna e inventario ampliado
          </Typography>
        </Box>
        <Chip label="Módulo habilitado" color="success" sx={{ fontWeight: 900, alignSelf: "flex-start" }} />
      </Stack>

      <Alert severity="info">
        El panel y la navegación ya respetan este módulo. Los modelos de ítems, movimientos y depósitos se incorporan en la siguiente etapa del backend sin mezclar esta capa con Productos.
      </Alert>

      <Grid container spacing={2.5}>
        {FEATURES.map((feature) => (
          <Grid item xs={12} md={6} key={feature.title}>
            <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,0.1)", color: BRAND.orange, mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                  {feature.title}
                </Typography>
                <Typography sx={{ color: "rgba(25,36,47,0.64)", mt: 1 }}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
